import { ECurrency } from '@ringana/configs';
import { ILogin } from '@/common/interfaces/login.interface';
import { ICustomer, ICustomerAddress } from '@/common/interfaces/customers.interface';
import { IShoppingCart } from '@/common/interfaces/cart.interface';
import { IPartner } from '@/common/interfaces/partners.interface';

class LocalStorageHelper {
  constructor() {
    const lastLocalStorageUpdateTime = localStorage.getItem(`rin-local-storage-last-update-time`);
    if(lastLocalStorageUpdateTime && (+lastLocalStorageUpdateTime < new Date().getTime() - 1000 * 60 * 60)) {
      this.removeCart();
      this.removeCartItemsCount();
      this.removeCartToBeReloaded();
      localStorage.setItem(`rin-local-storage-last-update-time`, new Date().getTime().toString());
      this.setCartToBeReloaded();
    }
  }

  setCartToBeReloaded(): void {
    localStorage.setItem(`rin-cart-has-to-be-reloaded`, `true`);
  }

  isCartToBeReloaded(): string {
    return localStorage.getItem(`rin-cart-has-to-be-reloaded`) ?? ``;
  }

  removeCartToBeReloaded(): void {
    return localStorage.removeItem(`rin-cart-has-to-be-reloaded`);
  }

  /**
   * returns the delivery addresses - if set in the last 15m
   * @returns {null|any}
   */
  getDeliveryAddresses(): ICustomerAddress[] {
    const deliveryAddressesUpdateTime = localStorage.getItem(`rin-delivery-addresses-update-time`);
    if(!deliveryAddressesUpdateTime || +deliveryAddressesUpdateTime < new Date().getTime() - 1000 * 60 * 15) {
      return [];
    }
    const data = localStorage.getItem(`rin-delivery-addresses`);
    if(!data) return [];
    return JSON.parse(data);
  }

  removeDeliveryAddresses(): void {
    localStorage.removeItem(`rin-delivery-addresses`);
  }

  setDeliveryAddresses(value: ICustomerAddress[]): void {
    localStorage.setItem(`rin-delivery-addresses-update-time`, new Date().getTime().toString());
    localStorage.setItem(`rin-delivery-addresses`, JSON.stringify(value));
  }

  getCart(): IShoppingCart | null {
    const cart = localStorage.getItem(`rin-cart`);
    if(!cart) return null;
    return JSON.parse(cart);
  }

  removeCart(): void {
    localStorage.removeItem(`rin-cart`);
  }

  setCart(cart: IShoppingCart): void {
    localStorage.setItem(`rin-cart`, JSON.stringify(cart));
  }

  getCartItemsCount(): number {
    const cartItemsCount = localStorage.getItem(`rin-cart-items`) ?? 0;
    return +cartItemsCount;
  }

  removeCartItemsCount(): void {
    localStorage.removeItem(`rin-cart-items`);
  }

  setCartItemsCount(itemsCount: string | number): void {
    localStorage.setItem(`rin-cart-items`, itemsCount.toString());
  }

  getLanguagesList(): string[] {
    const item = localStorage.getItem(`rin-languages-list`);
    if(item) return JSON.parse(item);
    return [];
  }

  setLanguagesList(value: string[]): void {
    localStorage.setItem(`rin-languages-list`, JSON.stringify(value));
  }

  getIsPartnerShop(): boolean | null {
    const item = localStorage.getItem(`rin-is-partnershop`);
    if(!item) {
      return null;
    }
    return JSON.parse(item);
  }

  removeIsPartnerShop(): void {
    localStorage.removeItem(`rin-is-partnershop`);
  }

  setIsPartnerShop(value: boolean): void {
    localStorage.setItem(`rin-is-partnershop`, JSON.stringify(value));
  }

  getPartnerShopInformation(): IPartner | null {
    const item = localStorage.getItem(`rin-partnershop-data`);
    if(!item) {
      return null;
    }
    return JSON.parse(item);
  }

  removePartnerShopInformation(): void {
    localStorage.removeItem(`rin-partnershop-data`);
  }

  setPartnerShopInformation(value: IPartner): void {
    localStorage.setItem(`rin-partnershop-data`, JSON.stringify(value));
  }

  isLocalStorageFilledCorrectly(): boolean {
    return !!this.getPersonalInfo();
  }

  getPersonalInfo(): ICustomer | null {
    const item = localStorage.getItem(`rin-personal-info`);
    if(!item) {
      return null;
    }
    return JSON.parse(item);
  }

  removePersonalInfo(): void {
    localStorage.removeItem(`rin-personal-info`);
    localStorage.removeItem(`rin-is-partner`);
  }

  setPersonalInfo(value: ICustomer | ILogin): void {
    localStorage.setItem(`rin-personal-info`, JSON.stringify(value));
    localStorage.setItem(`rin-is-partner`, value.isPartner ? `true` : `false`); // for legacy
  }

  /**
   * Add data to the personalInfo in LocalStorage
   * @param {object} data object contains customerData
   * @return {object}
   */
  upgradePersonalInfo(data: ICustomer): ICustomer {
    data.expandedPersonalInfo = true;
    let personalInfo = this.getPersonalInfo();
    personalInfo = (personalInfo === null) ? data : this.deepMerge(personalInfo, data) as ICustomer;
    localStorage.setItem(`rin-personal-info`, JSON.stringify(personalInfo));
    return personalInfo;
  }

  // TODO CHAX-615: Find better typings for this function
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deepMerge(target: any, source: any): unknown {
    for(const key of Object.keys(source)) {
      if(source[key] instanceof Object) {
        Object.assign(source[key], this.deepMerge(target[key], source[key]));
      }
    }
    Object.assign(target || {}, source);
    return target;
  }

  removeAll(): void {
    localStorage.clear();
  }

  removeLoginInformation(): void {
    this.removeDeliveryAddresses();
    this.removePersonalInfo();
  }

  getCurrency(): ECurrency | null {
    return localStorage.getItem(`rin-selected-currency`) as ECurrency ?? null;
  }

  setCurrency(value: string): void {
    localStorage.setItem(`rin-selected-currency`, value);
  }

  getNewNewsCount(): number {
    const newNewsCount = localStorage.getItem(`rin-new-news`) || 0;
    return +newNewsCount;
  }

  setNewNewsCount(newNewsCount: number| string): void {
    localStorage.setItem(`rin-new-news`, newNewsCount.toString());
  }

  removeNewNewsCount(): void {
    localStorage.removeItem(`rin-new-news`);
  }

  setNewNewsPostsIDS(newNewsPostsIDs: number[]): void {
    localStorage.setItem(`rin-new-news-posts-IDs`, JSON.stringify(newNewsPostsIDs));
  }

  getNewNewsPostsIDS(): number[] {
    const newNewsPostsIds = localStorage.getItem(`rin-new-news-posts-IDs`);
    if(newNewsPostsIds) {
      return JSON.parse(newNewsPostsIds);
    }
    return [];
  }
}

export default new LocalStorageHelper();
